import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmModal, EmailInput, showError, toast } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { flattenError } from '@feathr/hooks';
import { validate } from '@feathr/rachis';

export interface IExportModalProps {
  opened: boolean;
  onClose: () => void;
  exportType: 'monthly' | 'campaign' | 'daily';
  targetDate?: Date;
}

function EmailLimitsExportModal({
  opened,
  onClose,
  exportType,
  targetDate,
}: Readonly<IExportModalProps>): JSX.Element {
  const { t } = useTranslation();
  const { EmailUsageRecords } = useStore();
  const [email, setEmail] = useState<string>('');
  const [emailHadFocus, setEmailHadFocus] = useState<boolean>(false);

  function handleEmailBlur(): void {
    setEmailHadFocus(true);
  }

  function handleChangeEmail(newEmail?: string): void {
    setEmail(newEmail ?? '');
    // On first change start displaying validation errors
    setEmailHadFocus(true);
  }

  const handleExport = async (): Promise<void> => {
    try {
      switch (exportType) {
        case 'monthly':
          await EmailUsageRecords.exportMonthlyBreakdown({ email });
          break;

        case 'campaign':
          await EmailUsageRecords.exportCampaignBreakdown({
            email,
            targetDate: targetDate?.toISOString(),
          });
          break;

        case 'daily':
          await EmailUsageRecords.exportDailyEmailSends({
            email,
            targetDate: targetDate?.toISOString(),
          });
          break;

        default:
          throw new Error(t('Invalid export type'));
      }

      toast(
        t(
          'Export request submitted. You will receive an email with a link to download the CSV file.',
        ),
        {
          type: 'success',
        },
      );

      setEmail('');
      handleClose();
    } catch (error) {
      showError(error, t);
    }
  };

  const handleClose = (): void => {
    // Clear data
    setEmail('');
    setEmailHadFocus(false);
    // Close modal
    onClose();
  };

  const getModalTitle = (): string => {
    switch (exportType) {
      case 'monthly':
        return t('Export Monthly Breakdown');

      case 'campaign':
        return t('Export Campaign Breakdown');

      case 'daily':
        return t('Export Daily Email Sends');

      default:
        return t('Export Data');
    }
  };

  const validationErrors = ((): string[] => {
    const emailConstraint = {
      presence: {
        allowEmpty: false,
        message: '^Email address cannot be empty.',
      },
      email: {
        message: '^Email address is invalid.',
      },
    };
    const error = validate.single(email, emailConstraint);
    return flattenError(error) ?? [];
  })();

  return (
    <ConfirmModal
      cancelButtonText={t('Cancel')}
      confirmButtonText={t('Export')}
      confirmButtonType={'primary'}
      confirmDisabled={validationErrors.length > 0}
      onClose={handleClose}
      onConfirm={handleExport}
      opened={opened}
      size={'md'}
      t={t}
      title={getModalTitle()}
      validationError={validationErrors}
    >
      <EmailInput
        label={t('Email Address')}
        onBlur={handleEmailBlur}
        onChange={handleChangeEmail}
        t={t}
        validationError={emailHadFocus ? validationErrors : undefined}
        value={email}
      />
    </ConfirmModal>
  );
}

export default EmailLimitsExportModal;
